<template>
  <div>
    <PageDynamicBreadcrumb
      custom-current-page="Template Editor"
      :override-page-level="1"
    />

    <PageHeader title="Template Editor">
      <template v-slot:actions>
        <q-btn
          flat
          round
          icon="vertical_split"
          color="subtle"
          @click="showSettings = !showSettings"
        >
          <q-tooltip anchor="center left" self="center right">
            {{ showSettings ? "Hide Settings" : "Show Settings" }}
          </q-tooltip>
        </q-btn>
        <q-btn
          flat
          label="Export JSON"
          color="subtle"
          :disable="saving"
          @click="exportJson"
        />
        <q-btn
          label="Save Template"
          color="primary"
          :disable="saving || !canSubmit"
          :loading="saving"
          @click="save"
        />
      </template>
    </PageHeader>

    <div v-if="hydrating" class="row justify-center items-center">
      <q-spinner color="primary" size="3em" />
    </div>
    <div v-else class="row q-col-gutter-md q-mt-none">
      <!-- ---------- Settings Rail (toggleable) ---------- -->
      <div v-show="showSettings" class="col-12 col-md-3">
        <q-card flat bordered>
          <q-card-section>
            <q-input
              filled
              v-model="template.name"
              label="Template Name"
              :disable="saving"
            />
          </q-card-section>

          <q-separator />

          <q-card-section class="q-pb-none">
            <h6>Select A Canvas</h6>
          </q-card-section>
          <q-card-section>
            <div class="q-gutter-xs q-mb-md">
              <q-chip
                v-for="preset in sizePresets"
                :key="preset.label"
                clickable
                :outline="
                  !(template.width === preset.w && template.height === preset.h)
                "
                :color="
                  template.width === preset.w && template.height === preset.h
                    ? 'primary'
                    : 'grey-6'
                "
                :text-color="
                  template.width === preset.w && template.height === preset.h
                    ? 'white'
                    : ''
                "
                size="sm"
                @click="applyPreset(preset)"
              >
                {{ preset.label }}
              </q-chip>
            </div>

            <div class="row items-center no-wrap">
              <q-input
                filled
                dense
                type="number"
                v-model.number="template.width"
                label="Width (px)"
                min="16"
                max="4096"
              />
              <q-btn
                flat
                round
                icon="swap_horiz"
                color="subtle"
                class="q-mx-xs"
                @click="swapDims"
              >
                <q-tooltip>Swap width and height</q-tooltip>
              </q-btn>
              <q-input
                filled
                dense
                type="number"
                v-model.number="template.height"
                label="Height (px)"
                min="16"
                max="4096"
              />
            </div>
          </q-card-section>

          <q-separator />

          <q-card-section class="q-pb-none">
            <h6>Test Your Variables</h6>
            <div class="text-subtle q-mt-xs">
              Add <code>&#123;&#123;variables&#125;&#125;</code> in your HTML
              and they'll appear here.
            </div>
          </q-card-section>
          <q-card-section>
            <p v-if="!detectedVariables.length" class="text-subtle">
              No variables detected yet.
            </p>
            <div v-else class="q-gutter-y-sm">
              <q-input
                v-for="variable in detectedVariables"
                :key="variable"
                filled
                dense
                v-model="sampleData[variable]"
                :label="variable"
                spellcheck="false"
              />
            </div>
          </q-card-section>
        </q-card>
      </div>

      <!-- ---------- HTML Editor ---------- -->
      <div class="col-12" :class="showSettings ? 'col-md-4' : 'col-md-6'">
        <q-card flat bordered>
          <q-card-section class="q-pb-sm">
            <div class="row items-center justify-between">
              <h6>Template HTML</h6>
              <Label
                  :text="
                editorLineCount + ' lines · ' + template.html.length + ' chars'
              "
              />
            </div>
            <div class="text-subtle q-mt-xs">
              Full HTML/CSS is supported.
            </div>
            <div
              v-if="template.html && !htmlValidation.valid"
              class="text-negative q-mt-xs"
            >
              {{ htmlValidation.error }}
            </div>
          </q-card-section>
          <q-card-section class="q-pt-none">
            <textarea
              v-model="template.html"
              class="html-editor"
              spellcheck="false"
              autocapitalize="off"
              autocomplete="off"
              @keydown.tab.prevent="insertTab"
            ></textarea>
          </q-card-section>
        </q-card>
      </div>

      <!-- ---------- Preview ---------- -->
      <div class="col-12" :class="showSettings ? 'col-md-5' : 'col-md-6'">
        <TemplateEditorPreviewPane
          :html="interpolatedHtml"
          :width="template.width"
          :height="template.height"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/UI/PageHeader";
import PageDynamicBreadcrumb from "@/components/UI/PageDynamicBreadcrumb";
import Label from "@/components/UI/Label";
import TemplateEditorPreviewPane from "@/views/Templates/TemplateEditorPreviewPane.vue";


const VARIABLE_PATTERN = /{{\s*([a-zA-Z0-9_][a-zA-Z0-9_.-]*)\s*}}/g;

export default {
  name: "TemplateEditor",
  components: {
    TemplateEditorPreviewPane,
    PageDynamicBreadcrumb,
    PageHeader,
    Label
  },
  data() {
    return {
      template: {
        name: "",
        // Kept in the payload so render jobs route on the SQS side, and so
        // another engine could be added later without a schema change.
        renderer: "chromium",
        width: 1080,
        height: 1080,
        html: ""
      },
      sampleData: {},
      sizePresets: [
        { label: "Square 1080²", w: 1080, h: 1080 },
        { label: "OG 1200×630", w: 1200, h: 630 },
        { label: "Story 1080×1920", w: 1080, h: 1920 },
        { label: "Wide 1920×1080", w: 1920, h: 1080 }
      ],
      templateId: this.$route.params.templateId || "",
      hydrating: false,
      saving: false,
      showSettings: true
    };
  },
  computed: {
    /** Placeholder names found in the HTML. */
    detectedVariables() {
      const found = new Set();
      let match;
      VARIABLE_PATTERN.lastIndex = 0;
      while ((match = VARIABLE_PATTERN.exec(this.template.html)) !== null) {
        found.add(match[1]);
      }
      return [...found];
    },

    /** Template HTML with sample values substituted in. */
    interpolatedHtml() {
      // {{variable}} → sample value (untouched if no value yet, so gaps stay visible)
      return this.template.html.replace(VARIABLE_PATTERN, (whole, name) => {
        const value = this.sampleData[name];
        return value !== undefined && value !== "" ? value : whole;
      });
    },

    editorLineCount() {
      return this.template.html.split("\n").length;
    },

    /** Validate HTML structure. */
    htmlValidation() {
      const html = this.template.html.trim();
      if (!html) {
        return { valid: false, error: "HTML is required" };
      }

      // Check for common syntax issues that DOMParser might ignore

      // 1. Check for unclosed tags (basic check)
      const tagStack = [];
      const voidTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];
      const tagPattern = /<\/?([a-zA-Z][a-zA-Z0-9-]*)[^>]*>/g;
      let match;

      while ((match = tagPattern.exec(html)) !== null) {
        const fullTag = match[0];
        const tagName = match[1].toLowerCase();

        // Skip void tags and self-closing tags
        if (voidTags.includes(tagName) || fullTag.endsWith('/>')) {
          continue;
        }

        if (fullTag.startsWith('</')) {
          // Closing tag
          if (tagStack.length === 0 || tagStack[tagStack.length - 1] !== tagName) {
            return { valid: false, error: `Mismatched closing tag: </${tagName}>` };
          }
          tagStack.pop();
        } else {
          // Opening tag
          tagStack.push(tagName);
        }
      }

      if (tagStack.length > 0) {
        return { valid: false, error: `Unclosed tag: <${tagStack[tagStack.length - 1]}>` };
      }

      // 2. Check for incomplete tags (tags that don't end with >)
      const lastTag = html.lastIndexOf('<');
      if (lastTag !== -1) {
        const afterLastTag = html.substring(lastTag);
        if (!afterLastTag.includes('>')) {
          return { valid: false, error: "Incomplete HTML tag" };
        }
      }

      // 3. Use DOMParser for additional validation
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const parserError = doc.querySelector("parsererror");
      if (parserError) {
        return { valid: false, error: "Invalid HTML syntax" };
      }

      return { valid: true, error: null };
    },

    /** Check if required fields are filled to enable submit. */
    canSubmit() {
      return (
        this.template.name.trim() !== "" &&
        this.template.html.trim() !== "" &&
        this.htmlValidation.valid
      );
    }
  },
  mounted() {
    if (this.templateId) {
      this.hydrateFromStore();
    }
  },
  methods: {
    // ---------- hydration (edit mode) ----------
    hydrateFromStore() {
      this.hydrating = true;
      this.$store.dispatch("templates/POPULATE_LIST").finally(() => {
        let saved = this.$store.getters["templates/byId"][this.templateId];
        if (saved) {
          this.template = {
            name: saved.name,
            renderer: saved.renderer || "chromium",
            width: saved.width,
            height: saved.height,
            html: saved.html
          };
          this.sampleData = { ...(saved.sampleData || {}) };
          this.hydrating = false;
        } else {
          this.triggerActionOutcomeAlert(
            "update",
            false,
            "Template " + this.templateId + " could not be loaded."
          );
          this.$router.push("/templates");
        }
      });
    },

    // ---------- canvas ----------
    applyPreset(preset) {
      this.template.width = preset.w;
      this.template.height = preset.h;
    },
    swapDims() {
      const { width, height } = this.template;
      this.template.width = height;
      this.template.height = width;
    },

    // ---------- editor ----------
    insertTab(event) {
      const el = event.target;
      const { selectionStart: start, selectionEnd: end } = el;
      this.template.html =
        this.template.html.slice(0, start) +
        "  " +
        this.template.html.slice(end);
      this.$nextTick(() => {
        el.selectionStart = el.selectionEnd = start + 2;
      });
    },

    // ---------- persistence ----------
    buildPayload() {
      let payload = {
        name: this.template.name,
        renderer: this.template.renderer,
        width: this.template.width,
        height: this.template.height,
        html: this.template.html,
        sampleData: this.sampleData
      };
      if (this.templateId) {
        payload.id = this.templateId;
      }
      return payload;
    },
    save() {
      this.saving = true;
      this.$store
        .dispatch("templates/POST_REQUEST", {
          endpoint: this.templateId ? "update" : "create",
          params: this.buildPayload()
        })
        .then(() => {
          this.triggerActionOutcomeAlert("save", true);
          this.$router.push("/templates");
        })
        .catch(() => {
          this.triggerActionOutcomeAlert(
            "save",
            false,
            "Save failed — try again, or use Export JSON to keep a local copy."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    exportJson() {
      const blob = new Blob([JSON.stringify(this.buildPayload(), null, 2)], {
        type: "application/json"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        (this.template.name.replace(/\s+/g, "-").toLowerCase() || "template") +
        ".json";
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped lang="scss">
.html-editor {
  width: 100%;
  min-height: 520px;
  resize: vertical;
  background: color(gray, very-light);
  border: 1px solid color(border);
  border-radius: 4px;
  padding: 12px 14px;
  color: color(font);
  font-family: monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre;
  overflow: auto;
  tab-size: 2;
  @include transition(border-color);

  &:focus {
    outline: none;
    border-color: color(primary);
  }
}
</style>
